
import React, { useState } from 'react';
import { Plus, Edit, Trash2, X, Image, Link, Hash, ImageIcon } from 'lucide-react';
import styles from './BannerAds.module.css';
import { usePosters, useCreatePoster, useUpdatePoster, useDeletePoster } from '../../../hooks/usePosters';

const BannerAds = () => {
  const { posters, isLoading, refetchPosters } = usePosters();
  const createPosterMutation = useCreatePoster();
  const updatePosterMutation = useUpdatePoster();
  const deletePosterMutation = useDeletePoster();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPoster, setEditingPoster] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    rank: '',
    link: '',
    mobileImage: null,
    computerImage: null
  });
  const [imagePreviews, setImagePreviews] = useState({
    mobile: null,
    computer: null
  });

  const openModal = (poster = null) => {
    if (poster) {
      setEditingPoster(poster);
      setFormData({
        title: poster.title,
        rank: poster.rank || '',
        link: poster.link || '',
        mobileImage: null,
        computerImage: null
      });
      setImagePreviews({
        mobile: poster.mobile_image_url,
        computer: poster.computer_image_url
      });
    } else {
      setEditingPoster(null);
      setFormData({
        title: '',
        rank: '',
        link: '',
        mobileImage: null,
        computerImage: null
      });
      setImagePreviews({
        mobile: null,
        computer: null
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPoster(null);
    setFormData({
      title: '',
      rank: '',
      link: '',
      mobileImage: null,
      computerImage: null
    });
    setImagePreviews({
      mobile: null,
      computer: null
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [type]: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreviews(prev => ({
          ...prev,
          [type === 'mobileImage' ? 'mobile' : 'computer']: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: null
    }));
    setImagePreviews(prev => ({
      ...prev,
      [type === 'mobileImage' ? 'mobile' : 'computer']: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitFormData = new FormData();
    submitFormData.append('title', formData.title);
    submitFormData.append('rank', formData.rank || '0');
    submitFormData.append('link', formData.link || '');
    
    if (formData.mobileImage) {
      submitFormData.append('mobileImage', formData.mobileImage);
    }
    if (formData.computerImage) {
      submitFormData.append('computerImage', formData.computerImage);
    }

    try {
      if (editingPoster) {
        await updatePosterMutation.mutateAsync({ id: editingPoster.id, formData: submitFormData });
      } else {
        await createPosterMutation.mutateAsync(submitFormData);
      }
      closeModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleDelete = async (posterId) => {
    if (window.confirm('Are you sure you want to delete this poster?')) {
      try {
        await deletePosterMutation.mutateAsync(posterId);
      } catch (error) {
        console.error('Error deleting poster:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Banner Ads Management</h1>
        <button 
          className={styles.addButton}
          onClick={() => openModal()}
        >
          <Plus className={styles.addIcon} />
          Add New Poster
        </button>
      </div>

      {posters.length === 0 ? (
        <div className={styles.emptyState}>
          <ImageIcon className={styles.emptyStateIcon} />
          <div className={styles.emptyStateText}>No posters found</div>
          <div className={styles.emptyStateSubtext}>
            Click the "Add New Poster" button to create your first banner ad
          </div>
        </div>
      ) : (
        <div className={styles.postersGrid}>
          {posters.map((poster) => (
            <div key={poster.id} className={styles.posterCard}>
              <img 
                src={poster.mobile_image_url} 
                alt={poster.title}
                className={styles.posterImage}
                onError={(e) => {
                  e.target.src = poster.computer_image_url;
                }}
              />
              <div className={styles.posterInfo}>
                <h3 className={styles.posterTitle}>{poster.title}</h3>
                <div className={styles.posterDetails}>
                  <div className={styles.detailItem}>
                    <Hash className={styles.detailLabel} />
                    <span>Rank: {poster.rank || 0}</span>
                  </div>
                  {poster.link && (
                    <div className={styles.detailItem}>
                      <Link className={styles.detailLabel} />
                      <span>Link: {poster.link}</span>
                    </div>
                  )}
                  <div className={styles.detailItem}>
                    <span>Created: {formatDate(poster.created_at)}</span>
                  </div>
                </div>
                <div className={styles.actions}>
                  <button 
                    className={`${styles.actionButton} ${styles.editButton}`}
                    onClick={() => openModal(poster)}
                  >
                    <Edit />
                    Edit
                  </button>
                  <button 
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    onClick={() => handleDelete(poster.id)}
                  >
                    <Trash2 />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modal} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {editingPoster ? 'Edit Poster' : 'Add New Poster'}
              </h2>
              <button className={styles.closeButton} onClick={closeModal}>
                <X />
              </button>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  required
                  placeholder="Enter poster title"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Rank</label>
                <input
                  type="number"
                  name="rank"
                  value={formData.rank}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  placeholder="Enter rank (optional)"
                  min="0"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Link</label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  className={styles.formInput}
                  placeholder="Enter link (optional)"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Mobile Image *</label>
                <input
                  type="file"
                  id="mobileImage"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'mobileImage')}
                  className={styles.fileInput}
                />
                <label htmlFor="mobileImage" className={styles.fileInputLabel}>
                  <Image />
                  {imagePreviews.mobile ? 'Change Mobile Image' : 'Upload Mobile Image'}
                </label>
                {imagePreviews.mobile && (
                  <div className={styles.filePreview}>
                    <div className={styles.previewItem}>
                      <img 
                        src={imagePreviews.mobile} 
                        alt="Mobile preview" 
                        className={styles.previewImage}
                      />
                      <button 
                        type="button"
                        className={styles.removeFile}
                        onClick={() => removeFile('mobileImage')}
                      >
                        <X />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Computer Image *</label>
                <input
                  type="file"
                  id="computerImage"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'computerImage')}
                  className={styles.fileInput}
                />
                <label htmlFor="computerImage" className={styles.fileInputLabel}>
                  <Image />
                  {imagePreviews.computer ? 'Change Computer Image' : 'Upload Computer Image'}
                </label>
                {imagePreviews.computer && (
                  <div className={styles.filePreview}>
                    <div className={styles.previewItem}>
                      <img 
                        src={imagePreviews.computer} 
                        alt="Computer preview" 
                        className={styles.previewImage}
                      />
                      <button 
                        type="button"
                        className={styles.removeFile}
                        onClick={() => removeFile('computerImage')}
                      >
                        <X />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={
                  createPosterMutation.isLoading || 
                  updatePosterMutation.isLoading ||
                  !formData.title ||
                  (!formData.mobileImage && !imagePreviews.mobile) ||
                  (!formData.computerImage && !imagePreviews.computer)
                }
              >
                {createPosterMutation.isLoading || updatePosterMutation.isLoading
                  ? 'Saving...'
                  : editingPoster 
                    ? 'Update Poster' 
                    : 'Create Poster'
                }
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerAds;
